'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { User } from '@supabase/supabase-js';
import PersonnelEvaluationSystem from '@/components/personnel-evaluation-system';
import { useApiAuthorization } from '@/hooks/use-api-authorization';
import { supabaseClient } from '@/lib/supabase/client';

type AuthenticatedPersonnelPageProps = {
  initialTab: string;
  initialAdminUsersTab?:
    | 'store_assignment'
    | 'accounts'
    | 'evaluation_lines'
    | 'performance'
    | 'behavior'
    | 'adjustment'
    | 'rank_master';
};

const adminRoleSet = new Set([
  'admin',
  'administrator',
  '管理者',
  '人事管理者',
  'エリアマネージャー',
]);
const systemAdminRoleSet = new Set([
  'システム管理者',
  'system_admin',
  'super_admin',
]);

function resolveRole(rawRole: unknown): string {
  if (typeof rawRole !== 'string') return '';
  return rawRole.trim();
}

function resolveRoleId(rawRoleId: unknown): number | null {
  if (typeof rawRoleId === 'number' && Number.isInteger(rawRoleId)) {
    return rawRoleId;
  }
  if (typeof rawRoleId === 'string' && rawRoleId.trim() !== '') {
    const parsed = Number(rawRoleId);
    if (Number.isInteger(parsed)) return parsed;
  }
  return null;
}

function isSystemAdminUser(user: User): boolean {
  const roleId =
    resolveRoleId(user.user_metadata?.role_id) ??
    resolveRoleId(user.app_metadata?.role_id);
  if (roleId === 3) return true;

  const roleFromUserMeta = resolveRole(user.user_metadata?.role);
  const roleFromAppMeta = resolveRole(user.app_metadata?.role);
  const role = roleFromUserMeta || roleFromAppMeta;
  return role !== '' && systemAdminRoleSet.has(role);
}

function isAdminUser(user: User): boolean {
  const roleFromUserMeta = resolveRole(user.user_metadata?.role);
  const roleFromAppMeta = resolveRole(user.app_metadata?.role);
  const role = roleFromUserMeta || roleFromAppMeta;
  if (role && adminRoleSet.has(role)) return true;
  const roleId =
    resolveRoleId(user.user_metadata?.role_id) ??
    resolveRoleId(user.app_metadata?.role_id);
  if (roleId === 1) return true;
  return false;
}

function getDisplayRole(user: User, isAdmin: boolean, isSystemAdmin: boolean): string {
  const roleFromUserMeta = resolveRole(user.user_metadata?.role);
  const roleFromAppMeta = resolveRole(user.app_metadata?.role);
  const role = roleFromUserMeta || roleFromAppMeta;
  if (role) return role;
  const roleId =
    resolveRoleId(user.user_metadata?.role_id) ??
    resolveRoleId(user.app_metadata?.role_id);
  if (roleId === 1) return '管理者';
  if (roleId === 2) return '社員';
  if (roleId === 3) return 'システム管理者';
  if (isSystemAdmin) return 'システム管理者';
  return isAdmin ? 'System Admin' : 'Store Manager';
}

export default function AuthenticatedPersonnelPage({
  initialTab,
  initialAdminUsersTab = 'store_assignment',
}: AuthenticatedPersonnelPageProps) {
  useApiAuthorization();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [userMasterRoleId, setUserMasterRoleId] = useState<number | null>(null);
  const [userMasterName, setUserMasterName] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadUserMasterAuthState = async (userId: string) => {
      const userMasterResult = await supabaseClient
        .from('user_master')
        .select('name,role,must_change_password')
        .eq('id', userId)
        .maybeSingle();

      if (!isMounted) return;

      if (typeof userMasterResult.data?.role === 'number') {
        setUserMasterRoleId(userMasterResult.data.role);
      } else {
        setUserMasterRoleId(null);
      }

      setUserMasterName(
        typeof userMasterResult.data?.name === 'string' ? userMasterResult.data.name.trim() : '',
      );
      setMustChangePassword(Boolean(userMasterResult.data?.must_change_password));
    };

    const bootstrap = async () => {
      const { data } = await supabaseClient.auth.getSession();
      if (!isMounted) return;

      if (!data.session?.user) {
        router.replace('/login');
        setIsLoading(false);
        return;
      }

      setUser(data.session.user);
      await loadUserMasterAuthState(data.session.user.id);
      setIsLoading(false);
    };

    bootstrap();

    const { data: authListener } = supabaseClient.auth.onAuthStateChange(
      (_event, session) => {
        if (_event === 'TOKEN_REFRESHED') {
          return;
        }
        if (!session?.user) {
          setUser(null);
          setUserMasterRoleId(null);
          setMustChangePassword(false);
          router.replace('/login');
          return;
        }
        setUser(session.user);
        void loadUserMasterAuthState(session.user.id);
      },
    );

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [router]);

  useEffect(() => {
    if (!isLoading && user && mustChangePassword) {
      router.replace('/change-password');
    }
  }, [isLoading, mustChangePassword, router, user]);

  const resolved = useMemo(() => {
    if (!user) {
      return {
        isAdmin: false,
        showRoleToggle: false,
        effectiveTab: initialTab,
        displayUserName: '',
        displayRoleName: '',
        currentUserIdOverride: undefined as string | undefined,
      };
    }

    const isSystemAdmin =
      userMasterRoleId === 3 || (userMasterRoleId == null && isSystemAdminUser(user));
    const isAdmin =
      isSystemAdmin
        ? initialTab.startsWith('admin_')
        : userMasterRoleId === 1 || (userMasterRoleId == null && isAdminUser(user));
    const effectiveTab = isSystemAdmin
      ? (initialTab.startsWith('admin_') ? initialTab : 'admin_reports')
      : isAdmin
        ? (initialTab.startsWith('admin_') ? initialTab : 'admin_reports')
        : (initialTab.startsWith('admin_') ? 'dashboard' : initialTab);

    const displayUserName =
      userMasterName ||
      (typeof user.user_metadata?.full_name === 'string'
        ? user.user_metadata.full_name.trim()
        : '') ||
      'ログインユーザー';

    return {
      isAdmin,
      showRoleToggle: isSystemAdmin,
      effectiveTab,
      displayUserName,
      displayRoleName:
        userMasterRoleId === 1
          ? '管理者'
          : userMasterRoleId === 2
            ? '社員'
              : userMasterRoleId === 3
                ? 'システム管理者'
                : getDisplayRole(user, isAdmin, isSystemAdmin),
      currentUserIdOverride: undefined,
    };
  }, [initialTab, user, userMasterName, userMasterRoleId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-bold text-slate-700">認証状態を確認中...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;
  if (mustChangePassword) return null;

  const handleLogout = async () => {
    await supabaseClient.auth.signOut();
    router.replace('/login');
  };

  return (
    <PersonnelEvaluationSystem
      initialIsAdmin={resolved.isAdmin}
      initialTab={resolved.effectiveTab}
      initialAdminUsersTab={initialAdminUsersTab}
      showRoleToggle={resolved.showRoleToggle}
      displayUserName={resolved.displayUserName}
      displayRoleName={resolved.displayRoleName}
      currentUserIdOverride={resolved.currentUserIdOverride}
      authenticatedUserId={user.id}
      onLogout={handleLogout}
    />
  );
}
